import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { 
  ChatBubbleLeftIcon, 
  FunnelIcon,
  ChevronDownIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import RatingStars, { RatingDistribution } from './RatingStars';
import ReviewCard from './ReviewCard';
import ReviewForm from './ReviewForm';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

/**
 * ====================================================================
 * PRODUCT REVIEW SECTION
 * ====================================================================
 * Ürün detay sayfasında yorum bölümü
 * - Genel istatistikler ve rating distribution
 * - Filtreler (sıralama, fotoğraflı yorumlar, doğrulanmış alıcılar)
 * - Yorum listesi ve pagination
 * - Yorum yazma formu
 * - CheckoutPage modern tasarım dili
 * ====================================================================
 */

const ProductReviewSection = ({ productId, productName }) => {
  const { user, token } = useAuth();
  
  // States
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent'); // recent, helpful, rating_high, rating_low
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [filterVerified, setFilterVerified] = useState(false);
  const [filterWithImages, setFilterWithImages] = useState(false);
  const [filterRating, setFilterRating] = useState(0); // 0 = all, 1-5 = specific rating

  const LIMIT = 10;

  // Fetch reviews
  useEffect(() => {
    if (productId) {
      fetchReviews();
    }
  }, [productId, sortBy, page]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      
      const response = await api.get(`/api/products/${productId}/reviews`, {
        params: {
          limit: LIMIT,
          offset: page * LIMIT,
          sort: sortBy
        }
      });

      console.log('API Response:', response); // Debug
      
      if (response.success && response.data) {
        const { stats: statsData, reviews: reviewsData } = response.data;
        
        console.log('Stats:', statsData);
        console.log('Reviews:', reviewsData);
        
        // İlk sayfa mı kontrol et
        if (page === 0) {
          setReviews(reviewsData || []);
        } else {
          setReviews(prev => [...prev, ...(reviewsData || [])]);
        }
        
        setStats(statsData);
        setHasMore((reviewsData || []).length === LIMIT);
      }
    } catch (error) {
      console.error('Yorumlar yüklenirken hata:', error);
      toast.error('Yorumlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Filtreli reviews
  const filteredReviews = reviews.filter(review => {
    if (filterVerified && !review.is_verified) return false;
    if (filterWithImages && !review.has_images) return false;
    if (filterRating > 0 && review.rating !== filterRating) return false;
    return true;
  });


  // Load more
  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  // Sort değişimi
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setPage(0);
    setReviews([]);
  };

  if (!productId) {
    return null;
  }

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-b from-green-50 to-white px-6 py-8 border-b border-green-100">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <ChatBubbleLeftIcon className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  💬 Müşteri Yorumları
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  Gerçek müşteri deneyimleri
                </p>
            </div>
          </div>
          </div>
        </div>

      {/* Stats Section */}
      {stats && (
        <div className="p-6 border-b border-gray-100">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Overall Rating */}
            <div className="text-center md:border-r border-gray-200">
              <div className="text-5xl font-bold text-gray-900 mb-2">
                {stats.average_rating?.toFixed(1) || '0.0'}
              </div>
              <RatingStars 
                rating={stats.average_rating || 0} 
                size="lg" 
                readonly 
                className="justify-center mb-2"
              />
              <p className="text-sm text-gray-600">
                {stats.review_count || 0} değerlendirme
              </p>
            </div>

            {/* Rating Distribution */}
            <div className="md:col-span-2">
              <RatingDistribution distribution={stats.rating_distribution || {}} />
            </div>
          </div>
        </div>
      )}


      {/* Filters & Sort */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-gray-700">
              <FunnelIcon className="w-5 h-5" />
              <span className="font-semibold text-sm">Filtreler:</span>
            </div>
            
            <button
              onClick={() => setFilterVerified(!filterVerified)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                ${filterVerified 
                  ? 'bg-green-100 text-green-700 ring-2 ring-green-300' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
                }
              `}
            >
              <div className="flex items-center gap-2">
                <CheckBadgeIcon className="w-4 h-4" />
                <span>Doğrulanmış Alıcı</span>
              </div>
            </button>

            <button
              onClick={() => setFilterWithImages(!filterWithImages)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                ${filterWithImages 
                  ? 'bg-green-100 text-green-700 ring-2 ring-green-300' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
                }
              `}
            >
              📷 Fotoğraflı
            </button>

            {/* Star Rating Filter */}
            <div className="flex items-center gap-1 bg-white rounded-lg px-2 py-1 border border-gray-300">
              {[5, 4, 3, 2, 1].map(star => (
                <button
                  key={star}
                  onClick={() => setFilterRating(filterRating === star ? 0 : star)}
                  className={`
                    px-2 py-1 rounded text-sm font-medium transition-all duration-200
                    ${filterRating === star 
                      ? 'bg-yellow-100 text-yellow-700 ring-2 ring-yellow-300' 
                      : 'text-gray-600 hover:bg-gray-100'
                    }
                  `}
                >
                  {star}⭐
                </button>
              ))}
            </div>
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green transition-all cursor-pointer"
            >
              <option value="recent">En Yeni</option>
              <option value="helpful">En Yararlı</option>
              <option value="rating_high">En Yüksek Puan</option>
              <option value="rating_low">En Düşük Puan</option>
            </select>
            <ChevronDownIcon className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        {/* Active Filters Info */}
        {(filterVerified || filterWithImages || filterRating > 0) && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span className="font-medium">Gösterilen: {filteredReviews.length} / {reviews.length} yorum</span>
            {filterRating > 0 && (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                {filterRating} Yıldız
              </span>
            )}
          </div>
        )}
      </div>

      {/* Reviews List */}
      <div className="divide-y divide-gray-200">
        {loading && page === 0 ? (
          // Loading Skeleton
          <div className="p-6 space-y-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-24" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-3 bg-gray-200 rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredReviews.length > 0 ? (
          <>
            {filteredReviews.map(review => (
              <ReviewCard 
                key={review.id} 
                review={review}
                productId={productId}
              />
            ))}

            {/* Load More Button */}
            {hasMore && !loading && (
              <div className="p-6 text-center">
                <button
                  onClick={handleLoadMore}
                  className="bg-brand-green text-white px-8 py-3 rounded-xl font-semibold hover:bg-green-600 hover:shadow-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
                >
                  Daha Fazla Yükle
                </button>
              </div>
            )}

            {loading && page > 0 && (
              <div className="p-6 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-brand-green border-t-transparent" />
              </div>
            )}
          </>
        ) : (
          // Empty State
          <div className="p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-green-100 to-green-50 rounded-full flex items-center justify-center">
              <ChatBubbleLeftIcon className="w-10 h-10 text-brand-green" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {filterVerified || filterWithImages || filterRating > 0
                ? 'Bu filtrelere uygun yorum bulunamadı'
                : 'Henüz değerlendirme yok'
              }
            </h3>
            <p className="text-gray-600 mb-6">
              {filterVerified || filterWithImages || filterRating > 0
                ? 'Filtreleri kaldırarak tüm yorumları görebilirsiniz'
                : 'Henüz bu ürün için yorum yazılmamış'
              }
            </p>
            <p className="text-sm text-gray-500">
              📦 Ürünü sipariş ettikten ve teslim aldıktan sonra &quot;Siparişlerim&quot; sayfasından yorum yazabilirsiniz.
            </p>
          </div>
        )}
      </div>

    </div>
  </>
  );
};

export default ProductReviewSection;
